import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DEMO_LOGINS, ELoginAs, LIVE_LOGINS_WITHOUT_CRM } from '@aqx-core/common/api/auth';
import { AUTHENTICATION_ROUTES } from '@aqx-core/common/constants/router';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { useAuthRoutes } from '@aqx-core/common/hooks/auth/use-auth-routes';
import { useAuthLoading } from '@aqx-core/common/services/auth';
import { useCompanyRegistrationUrl } from '@aqx-core/common/services/company';
import { openUrl } from '@aqx-member/web/utils/url/open-url';

import AuthFormSecondaryAction from '../../shared/form-secondary-action';

interface ILoginSignUpAction {
  selectedTab: ELoginAs | undefined;
}

const LoginSignUpAction: FCC<ILoginSignUpAction> = ({ selectedTab }) => {
  const { t } = useTranslation();
  const { hasCrmSignUpButton, hasCrmSignUpRoute, hasLiveSignUpButton, hasDemoSignUpButton, hasDemoSignUpRoute } =
    useAuthRoutes();
  const registrationUrl = useCompanyRegistrationUrl(selectedTab);

  const navigate = useNavigate();

  const authLoginLoading = useAuthLoading();

  const {
    text: signUpText,
    onClick: onClickSignUp,
    dataTestId
  } = useMemo(() => {
    const OPEN_DEMO = t('Open Demo Account');
    const SIGN_UP = t('Sign Up');

    // CRM
    if (selectedTab === ELoginAs.LIVE && hasCrmSignUpButton) {
      const onClick = () =>
        hasCrmSignUpRoute ? navigate(AUTHENTICATION_ROUTES.SIGN_UP_CRM) : openUrl(registrationUrl, '_blank');

      return { text: SIGN_UP, onClick, dataTestId: EDataTestId.LOGIN_ACCOUNT_SIGNUP };
    }
    if (selectedTab && LIVE_LOGINS_WITHOUT_CRM.includes(selectedTab) && hasLiveSignUpButton) {
      const onClick = () => openUrl(registrationUrl, '_blank');

      return { text: SIGN_UP, onClick, dataTestId: EDataTestId.LOGIN_ACCOUNT_SIGNUP };
    }
    if (selectedTab && DEMO_LOGINS.includes(selectedTab) && hasDemoSignUpButton) {
      const onClick = () =>
        hasDemoSignUpRoute ? navigate(AUTHENTICATION_ROUTES.SIGN_UP_DEMO) : openUrl(registrationUrl, '_blank');
      return { text: OPEN_DEMO, onClick };
    }
    return { text: '', onClick: () => {} };
  }, [
    t,
    selectedTab,
    hasCrmSignUpButton,
    hasLiveSignUpButton,
    hasDemoSignUpButton,
    hasCrmSignUpRoute,
    navigate,
    registrationUrl,
    hasDemoSignUpRoute
  ]);

  if (!signUpText) {
    return null;
  }

  return (
    <AuthFormSecondaryAction
      infoText={t("Don't have an account?")}
      actionText={signUpText}
      onClickActionText={onClickSignUp}
      disabled={authLoginLoading}
      dataTestId={dataTestId}
    />
  );
};

export default LoginSignUpAction;
